import { Article } from '../types/article';
import apiBaseUrl from './apiConfig';

// Cache keys
const CACHE_KEYS = {
  FEATURED_ARTICLES: 'featured_articles',
  ALL_ARTICLES: 'all_articles',
};

// Cache helpers
const setToCache = <T>(key: string, data: T, expirationMs: number = 5 * 60 * 1000): void => {
  const item = {
    value: data,
    expiry: Date.now() + expirationMs,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

const getFromCache = <T>(key: string): T | null => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;

  const item = JSON.parse(itemStr);
  if (Date.now() > item.expiry) {
    localStorage.removeItem(key);
    return null;
  }
  
  return item.value as T;
};

/**
 * Base fetch function with error handling and retries
 */
async function fetchWithRetry<T>(
  endpoint: string, 
  options: RequestInit = {}, 
  retries = 2
): Promise<T> {
  try {
    const response = await fetch(`${apiBaseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (retries > 0) {
      return fetchWithRetry<T>(endpoint, options, retries - 1);
    }
    throw error;
  }
}

/**
 * Fetches featured articles from the backend
 */
export const fetchFeaturedArticles = async (limit: number = 5): Promise<Article[]> => {
  try {
    // Try to get from cache first
    const cachedArticles = getFromCache<Article[]>(CACHE_KEYS.FEATURED_ARTICLES);
    if (cachedArticles) {
      console.log('Using cached featured articles');
      return cachedArticles;
    }

    // Fetch from backend API
    const articles = await fetchWithRetry<Article[]>(`/featured?limit=${limit}`);
    
    // Cache the result
    setToCache(CACHE_KEYS.FEATURED_ARTICLES, articles);
    
    return articles;
  } catch (error) {
    console.error('Error fetching featured articles:', error);
    return [];
  }
};

/**
 * Fetches all articles from the backend
 */
export const fetchAllArticles = async (): Promise<Article[]> => {
  try {
    // Try to get from cache first
    const cachedArticles = getFromCache<Article[]>(CACHE_KEYS.ALL_ARTICLES);
    if (cachedArticles) {
      console.log('Using cached articles');
      return cachedArticles;
    }

    // Fetch from backend API
    const articles = await fetchWithRetry<Article[]>(`/`);
    
    // Cache the result
    setToCache(CACHE_KEYS.ALL_ARTICLES, articles);
    
    return articles;
  } catch (error) {
    console.error('Error fetching all articles:', error);
    return [];
  }
};

/**
 * Fetch a single article by slug
 */
export const fetchArticleBySlug = async (slug: string): Promise<Article | null> => {
  try {
    const article = await fetchWithRetry<Article | null>(`/${slug}`);
    return article;
  } catch (error) {
    console.error(`Error fetching article with slug ${slug}:`, error);
    return null;
  }
};

/**
 * Fetch articles by category
 */
export const fetchArticlesByCategory = async (categorySlug: string): Promise<Article[]> => {
  try {
    const articles = await fetchWithRetry<Article[]>(`/category/${categorySlug}`);
    return articles;
  } catch (error) {
    console.error(`Error fetching articles for category ${categorySlug}:`, error);
    return [];
  }
};

/**
 * Fetch articles by tag
 */
export const fetchArticlesByTag = async (tagName: string): Promise<Article[]> => {
  try {
    const articles = await fetchWithRetry<Article[]>(`/tag/${encodeURIComponent(tagName)}`);
    return articles;
  } catch (error) {
    console.error(`Error fetching articles for tag ${tagName}:`, error);
    return [];
  }
};

/**
 * Fetch related articles
 */
export const fetchRelatedArticles = async (
  category: string, 
  excludeSlug: string
): Promise<Article[]> => {
  try {
    const articles = await fetchWithRetry<Article[]>(
      `/related/${category}?excludeSlug=${excludeSlug}`
    );
    return articles;
  } catch (error) {
    console.error(`Error fetching related articles:`, error);
    return [];
  }
};

/**
 * Fetch recent articles
 */
export const fetchRecentArticles = async (daysBack: number = 7): Promise<Article[]> => {
  try {
    const articles = await fetchWithRetry<Article[]>(`/recent?days=${daysBack}`);
    return articles;
  } catch (error) {
    console.error('Error fetching recent articles:', error);
    return [];
  }
};

/**
 * Search articles
 */
export const searchArticles = async (query: string): Promise<Article[]> => {
  try {
    const articles = await fetchWithRetry<Article[]>(`/search?q=${encodeURIComponent(query)}`);
    return articles;
  } catch (error) {
    console.error(`Error searching articles with query "${query}":`, error);
    return [];
  }
};